const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

// Breathing room between a pinned header and the element brought under it.
const GAP = 8
// How deep into the scroller's top a pinned header is looked for.
const PROBE_DEPTH = 160

const isScroller = (element: HTMLElement) => {
  const overflowY = getComputedStyle(element).overflowY
  return (
    (overflowY === "auto" || overflowY === "scroll") &&
    element.scrollHeight > element.clientHeight
  )
}

const scrollParent = (element: HTMLElement): HTMLElement | null => {
  for (
    let parent = element.parentElement;
    parent;
    parent = parent.parentElement
  ) {
    if (isScroller(parent)) return parent
  }
  return null
}

// How far down the scroller's top edge something pinned will reach once it
// sticks (the Settings title and tabs), so a target is not scrolled under it.
// Probes the top band, since padding can sit above the header.
const pinnedHeight = (
  scroller: HTMLElement,
  target: HTMLElement,
  x: number
) => {
  if (typeof document.elementFromPoint !== "function") return 0
  const top = scroller.getBoundingClientRect().top
  for (let y = top + 1; y < top + PROBE_DEPTH; y += GAP) {
    for (
      let hit = document.elementFromPoint(x, y);
      hit && hit !== scroller;
      hit = hit.parentElement
    ) {
      if (hit.contains(target)) break
      const style = getComputedStyle(hit)
      const box = hit.getBoundingClientRect()
      if (style.position === "fixed") return Math.max(0, box.bottom - top)
      if (style.position === "sticky")
        return Math.max(0, (parseFloat(style.top) || 0) + box.height)
    }
  }
  return 0
}

// Brings an element to the top of its own scroll container, clear of any
// pinned header (no smooth scroll under reduced motion), then focuses it — or
// the separate element named by `focusId`. Only that container moves:
// `scrollIntoView` also scrolls every ancestor, which shifted the whole app
// frame on tablet widths.
export const scrollToAndFocus = (
  elementId: string,
  focusId: string = elementId
) => {
  const element = document.getElementById(elementId)
  if (element) {
    const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth"
    const scroller = scrollParent(element)
    if (scroller) {
      const target = element.getBoundingClientRect()
      const offset = pinnedHeight(scroller, element, target.left + GAP)
      const top =
        target.top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        offset -
        GAP
      scroller.scrollTo({ top: Math.max(0, top), behavior })
    } else {
      element.scrollIntoView?.({ behavior, block: "start" })
    }
  }
  document.getElementById(focusId)?.focus({ preventScroll: true })
}
